// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\SupplierDetail.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ConfirmDialog from "../components/ConfirmDialog";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatInteger = (value) => integerFormatter.format(Number(value || 0));
const formatDate = (value) => value || "-";
const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const formatFileSize = (value) => {
  const size = Number(value || 0);

  if (!size) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${formatNumber(size / 1024)} KB`;

  return `${formatNumber(size / (1024 * 1024))} MB`;
};
const getDocumentExtension = (document) => {
  const filename = String(document?.original_filename || "").toLowerCase();
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop() : "";
};
const getPreviewKind = (document) => {
  const mimeType = String(document?.mime_type || "").toLowerCase();
  const extension = getDocumentExtension(document);

  if (mimeType.includes("pdf") || extension === "pdf") return "pdf";
  if (mimeType.startsWith("image/") || ["jpg", "jpeg", "png"].includes(extension)) {
    return "image";
  }

  return "unsupported";
};
const getPreviewContentType = (document, previewKind) => {
  const mimeType = String(document?.mime_type || "").trim();

  if (mimeType) return mimeType;
  if (previewKind === "pdf") return "application/pdf";
  if (previewKind === "image") {
    const extension = getDocumentExtension(document);
    if (extension === "jpg") return "image/jpeg";
    if (extension === "jpeg" || extension === "png") return `image/${extension}`;
  }

  return "";
};
const PURCHASE_HISTORY_PAGE_SIZE = 10;
const DESCRIPTION_TRUNCATE_LENGTH = 72;
const API_URL = getApiBaseUrl();

const getMonthKey = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "-";

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) return monthKey;

  return date.toLocaleDateString("es-ES", {
    month: "short",
    year: "numeric",
  });
};

const getPartDetailPath = (materialId) =>
  materialId ? `/parts/${encodeURIComponent(materialId)}` : "#";

const getMaterialLabel = (row) => {
  if (row.material_id && row.description) {
    return `${row.material_id} - ${row.description}`;
  }

  return row.material_id || row.description || "Sin material";
};

const getSortIndicator = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "";
  }

  return sortConfig.direction === "asc" ? " ▲" : " ▼";
};

const truncateText = (value, maxLength = DESCRIPTION_TRUNCATE_LENGTH) => {
  const text = String(value || "").trim();

  if (!text) return "-";
  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3)}...`;
};

const SupplierDetail = () => {
  const { supplier_id: supplierId } = useParams();
  const navigate = useNavigate();
  const { store, actions } = useContext(Context);
  const [purchaseHistoryPage, setPurchaseHistoryPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [documentPreview, setDocumentPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const authRole = String(store.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();

  const isAdmin = authRole === "admin";
  const canViewDocuments = authRole === "admin" || authRole === "tecnico";
  const token = store.token;

  useEffect(() => {
    if (canViewDocuments) {
      actions.getSupplierDocuments(supplierId);
    }

    return () => {
      actions.clearSupplierDocuments();
    };
  }, [supplierId, canViewDocuments]);

  useEffect(() => {
    const controller = new AbortController();

    const loadDetail = async () => {
      if (!token) {
        setDetail(null);
        return;
      }

      const params = new URLSearchParams({
        page: String(purchaseHistoryPage),
        per_page: String(PURCHASE_HISTORY_PAGE_SIZE),
      });
      if (sortConfig.key) {
        params.set("sort_by", sortConfig.key);
        params.set("sort_order", sortConfig.direction);
      }

      setDetailLoading(true);
      setDetailError("");

      try {
        const response = await fetch(
          `${API_URL}/api/catalog/suppliers/${encodeURIComponent(supplierId)}?${params.toString()}`,
          {
            headers: { Authorization: "Bearer " + token },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setDetail(null);
          setDetailError(payload?.error || "No se pudo cargar el proveedor");
          return;
        }

        setDetail(payload.data || null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Supplier detail load error:", error);
        setDetail(null);
        setDetailError("No se pudo cargar el proveedor");
      } finally {
        if (!controller.signal.aborted) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => controller.abort();
  }, [purchaseHistoryPage, sortConfig, supplierId, token]);

  useEffect(
    () => () => {
      if (documentPreview?.objectUrl) {
        window.URL.revokeObjectURL(documentPreview.objectUrl);
      }
    },
    [documentPreview?.objectUrl],
  );

  const summary = detail?.summary || null;
  const purchaseHistory = Array.isArray(detail?.purchase_history?.rows)
    ? detail.purchase_history.rows
    : [];
  const purchaseHistoryAll = Array.isArray(detail?.purchase_history_all)
    ? detail.purchase_history_all
    : purchaseHistory;
  const purchaseHistoryPagination = detail?.purchase_history?.pagination || {
    page: 1,
    per_page: PURCHASE_HISTORY_PAGE_SIZE,
    total: purchaseHistory.length,
    pages: 1,
  };
  const topParts = Array.isArray(detail?.top_parts) ? detail.top_parts : [];
  const supplierDocuments = Array.isArray(store.supplierDocuments)
    ? store.supplierDocuments
    : [];

  useEffect(() => {
    setPurchaseHistoryPage(1);
  }, [supplierId]);

  const analytics = useMemo(() => {
    const totalSpend = Number(summary?.total_spend || 0);
    const purchaseLines = Number(summary?.purchase_count || purchaseHistoryAll.length || 0);
    const distinctParts = Number(summary?.distinct_parts || 0);
    const averageSpendPerLine =
      purchaseLines > 0 ? totalSpend / purchaseLines : 0;
    const monthlySpendMap = new Map();

    purchaseHistoryAll.forEach((row) => {
      const monthKey = getMonthKey(row.date);
      if (!monthKey) return;

      const currentMonth = monthlySpendMap.get(monthKey) || {
        monthKey,
        month: formatMonthLabel(monthKey),
        totalSpend: 0,
        purchaseLines: 0,
      };

      currentMonth.totalSpend += Number(row.total_price || 0);
      currentMonth.purchaseLines += 1;
      monthlySpendMap.set(monthKey, currentMonth);
    });

    const monthlySpend = Array.from(monthlySpendMap.values()).sort((a, b) =>
      a.monthKey.localeCompare(b.monthKey),
    );
    const topPartsBySpend = topParts.slice(0, 8).map((row) => ({
      ...row,
      materialLabel: getMaterialLabel(row),
      total_spend: Number(row.total_spend || 0),
      totalSpend: Number(row.total_spend || 0),
      totalQuantity: Number(row.total_quantity || 0),
      purchaseCount: Number(row.purchase_count || 0),
    }));

    return {
      kpis: {
        totalSpend,
        purchaseLines,
        distinctParts,
        averageSpendPerLine,
      },
      monthlySpend,
      topPartsBySpend,
    };
  }, [purchaseHistoryAll, summary, topParts]);

  const totalPurchaseHistoryPages = Math.max(
    1,
    Number(purchaseHistoryPagination.pages || 1),
  );
  const currentPurchaseHistoryPage = Math.min(
    Number(purchaseHistoryPagination.page || purchaseHistoryPage),
    totalPurchaseHistoryPages,
  );
  const paginatedPurchaseHistory = purchaseHistory;

  const handleSort = async (key) => {
    const nextSortConfig = {
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    };
    setSortConfig(nextSortConfig);
    setPurchaseHistoryPage(1);
  };

  const handleDocumentFileChange = (event) => {
    setSelectedDocumentFile(event.target.files?.[0] || null);
  };

  const handleDocumentUpload = async (event) => {
    event.preventDefault();

    if (!selectedDocumentFile) {
      actions.showToast("Selecciona un archivo", "warning");
      return;
    }

    const result = await actions.uploadSupplierDocument(supplierId, {
      file: selectedDocumentFile,
      documentType,
    });

    if (result.success) {
      setSelectedDocumentFile(null);
      setDocumentType("");
      event.target.reset();
      await actions.getSupplierDocuments(supplierId);
    }
  };

  const handleDocumentDownload = async (documentId) => {
    await actions.downloadSupplierDocument(supplierId, documentId);
  };

  const handleDocumentPreviewClose = () => {
    if (documentPreview?.objectUrl) {
      window.URL.revokeObjectURL(documentPreview.objectUrl);
    }
    setDocumentPreview(null);
  };

  const handleDocumentPreview = async (document) => {
    const previewKind = getPreviewKind(document);

    if (documentPreview?.objectUrl) {
      window.URL.revokeObjectURL(documentPreview.objectUrl);
    }

    setDocumentPreview({
      document,
      previewKind,
      loading: previewKind !== "unsupported",
      objectUrl: null,
      error: null,
    });

    if (previewKind === "unsupported") {
      return;
    }

    const result = await actions.previewSupplierDocument(
      supplierId,
      document.id,
      getPreviewContentType(document, previewKind),
    );

    if (!result.success) {
      setDocumentPreview({
        document,
        previewKind,
        loading: false,
        objectUrl: null,
        error: result.error || "Error cargando vista previa",
      });
      return;
    }

    setDocumentPreview({
      document,
      previewKind,
      loading: false,
      objectUrl: result.data?.objectUrl || null,
      contentType: result.data?.contentType || document.mime_type || "",
      filename: result.data?.filename || document.original_filename || "",
      error: null,
    });
  };

  const handleDocumentDelete = async () => {
    const document = documentToDelete;
    if (!document) {
      return;
    }

    setDocumentToDelete(null);
    const result = await actions.deleteSupplierDocument(supplierId, document.id);

    if (result.success) {
      await actions.getSupplierDocuments(supplierId);
    }
  };

  const documentsBlock = canViewDocuments ? (
    <section className="catalog-card part-section-card supplier-documents-card">
      <div className="catalog-section-head supplier-documents-head">
        <div>
          <h2>Documentación del proveedor</h2>
          <p>Documentos asociados al proveedor disponibles para consulta.</p>
        </div>
        {isAdmin && (
          <form
            className="supplier-documents-upload"
            onSubmit={handleDocumentUpload}
          >
            <div className="supplier-documents-upload-fields">
              <input
                className="form-control form-control-sm"
                type="file"
                onChange={handleDocumentFileChange}
                disabled={store.supplierDocumentUploadLoading}
              />
              <input
                className="form-control form-control-sm"
                type="text"
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value)}
                placeholder="Tipo de documento"
                disabled={store.supplierDocumentUploadLoading}
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              type="submit"
              disabled={
                store.supplierDocumentUploadLoading ||
                !selectedDocumentFile
              }
            >
              {store.supplierDocumentUploadLoading
                ? "Subiendo..."
                : "Subir documento"}
            </button>
          </form>
        )}
      </div>

      {store.supplierDocumentUploadError && (
        <div className="catalog-state catalog-state-error catalog-state-inline">
          {store.supplierDocumentUploadError}
        </div>
      )}

      {store.supplierDocumentActionError && (
        <div className="catalog-state catalog-state-error catalog-state-inline">
          {store.supplierDocumentActionError}
        </div>
      )}

      {store.supplierDocumentsLoading && (
        <div className="catalog-state catalog-state-loading catalog-state-inline">
          Cargando documentación del proveedor...
        </div>
      )}

      {!store.supplierDocumentsLoading && store.supplierDocumentsError && (
        <div className="catalog-state catalog-state-error catalog-state-inline">
          {store.supplierDocumentsError}
        </div>
      )}

      {!store.supplierDocumentsLoading &&
        !store.supplierDocumentsError &&
        supplierDocuments.length === 0 && (
          <div className="catalog-state catalog-state-empty catalog-state-inline">
            No hay documentos registrados para este proveedor.
          </div>
        )}

      {!store.supplierDocumentsLoading &&
        !store.supplierDocumentsError &&
        supplierDocuments.length > 0 && (
          <div className="table-responsive catalog-table-scroll supplier-documents-table-wrap">
            <table className="table table-compact mb-0 supplier-documents-table">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th className="text-end">Tamaño</th>
                  <th>Fecha subida</th>
                  <th>Usuario subida</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {supplierDocuments.map((document) => (
                  <tr key={document.id}>
                    <td className="supplier-document-name">
                      {document.original_filename || "-"}
                    </td>
                    <td>{document.document_type || "-"}</td>
                    <td className="text-end">
                      {formatFileSize(document.file_size)}
                    </td>
                    <td>{formatDateTime(document.uploaded_at)}</td>
                    <td>
                      {document.uploaded_by_username ||
                        document.uploaded_by_name ||
                        document.uploaded_by ||
                        "-"}
                    </td>
                    <td className="text-end">
                      <div className="supplier-document-actions">
                        <button
                          type="button"
                          className="catalog-detail-button"
                          onClick={() => handleDocumentPreview(document)}
                        >
                          Ver
                        </button>
                        <button
                          type="button"
                          className="catalog-detail-button"
                          onClick={() => handleDocumentDownload(document.id)}
                          disabled={
                            store.supplierDocumentDownloadingId ===
                            document.id
                          }
                        >
                          {store.supplierDocumentDownloadingId === document.id
                            ? "Descargando..."
                            : "Descargar"}
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            className="catalog-detail-button supplier-document-delete-button"
                            onClick={() => setDocumentToDelete(document)}
                            disabled={
                              store.supplierDocumentDeletingId === document.id
                            }
                          >
                            {store.supplierDocumentDeletingId === document.id
                              ? "Eliminando..."
                              : "Eliminar"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  ) : null;

  const documentPreviewModal = canViewDocuments && documentPreview ? (
    <div
      className="supplier-document-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleDocumentPreviewClose();
        }
      }}
    >
      <section
        className="supplier-document-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplier-document-preview-title"
      >
        <div className="supplier-document-modal-header">
          <div>
            <h2 id="supplier-document-preview-title">Vista previa</h2>
            <p>{documentPreview.document?.original_filename || "-"}</p>
          </div>
          <button
            type="button"
            className="catalog-detail-button"
            onClick={handleDocumentPreviewClose}
          >
            Cerrar
          </button>
        </div>

        <div className="supplier-document-preview-body">
          {documentPreview.loading && (
            <div className="catalog-state catalog-state-loading catalog-state-inline">
              Cargando vista previa...
            </div>
          )}

          {!documentPreview.loading && documentPreview.error && (
            <div className="catalog-state catalog-state-error catalog-state-inline">
              {documentPreview.error}
            </div>
          )}

          {!documentPreview.loading &&
            !documentPreview.error &&
            documentPreview.previewKind === "unsupported" && (
              <div className="catalog-state catalog-state-empty catalog-state-inline">
                Vista previa no disponible para este tipo de archivo.
                Descargue el documento.
              </div>
            )}

          {!documentPreview.loading &&
            !documentPreview.error &&
            documentPreview.previewKind === "pdf" &&
            documentPreview.objectUrl && (
              <iframe
                className="supplier-document-preview-frame"
                src={documentPreview.objectUrl}
                title={documentPreview.filename || "Vista previa PDF"}
              />
            )}

          {!documentPreview.loading &&
            !documentPreview.error &&
            documentPreview.previewKind === "image" &&
            documentPreview.objectUrl && (
              <img
                className="supplier-document-preview-image"
                src={documentPreview.objectUrl}
                alt={documentPreview.filename || "Vista previa del documento"}
              />
            )}
        </div>

        <div className="supplier-document-modal-footer">
          <button
            type="button"
            className="catalog-detail-button"
            onClick={() =>
              handleDocumentDownload(documentPreview.document?.id)
            }
            disabled={
              store.supplierDocumentDownloadingId ===
              documentPreview.document?.id
            }
          >
            {store.supplierDocumentDownloadingId ===
            documentPreview.document?.id
              ? "Descargando..."
              : "Descargar"}
          </button>
        </div>
      </section>
    </div>
  ) : null;

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
      >
        {label}
        {getSortIndicator(sortConfig, key)}
      </button>
    </th>
  );

  return (
    <div className="container mt-3 catalog-page part-detail-page supplier-detail-page">
      <div className="page-header catalog-header supplier-detail-header">
        <div className="supplier-detail-heading">
          <div className="supplier-detail-title-row">
            <button
              type="button"
              className="btn btn-secondary supplier-detail-back-button"
              onClick={() => navigate("/suppliers-catalog")}
            >
              ← Volver
            </button>
            <span className="supplier-detail-chip">Proveedor</span>
            <h1 className="page-title supplier-detail-title">
              {summary?.supplier_name || "Sin proveedor"}
            </h1>
            <span className="supplier-detail-id">
              ID {summary?.supplier_id || supplierId || "-"}
            </span>
          </div>
          {summary && (
            <div className="supplier-detail-period" aria-label="Periodo de compras">
              <span>
                Última compra <strong>{formatDate(summary.last_purchase_date)}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {detailLoading && (
        <div className="catalog-state catalog-state-loading">
          Cargando detalle del proveedor...
        </div>
      )}

      {!detailLoading && detailError && (
        <div className="catalog-state catalog-state-error">
          {detailError}
        </div>
      )}

      {!detailLoading && !detailError && !summary && (
        <div className="catalog-state catalog-state-empty">
          No hay datos disponibles para este proveedor.
        </div>
      )}

      {!detailLoading && !detailError && summary && (
        <>
          <section className="supplier-analytics-kpis">
            <article className="supplier-analytics-kpi supplier-analytics-kpi-spend">
              <span className="supplier-analytics-kpi-label">Gasto total proveedor</span>
              <strong>{formatCurrency(analytics.kpis.totalSpend)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-lines">
              <span className="supplier-analytics-kpi-label">Líneas de compra</span>
              <strong>{formatInteger(analytics.kpis.purchaseLines)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-parts">
              <span className="supplier-analytics-kpi-label">Repuestos distintos</span>
              <strong>{formatInteger(analytics.kpis.distinctParts)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-average">
              <span className="supplier-analytics-kpi-label">Gasto medio por línea</span>
              <strong>{formatCurrency(analytics.kpis.averageSpendPerLine)}</strong>
            </article>
          </section>

          <section className="supplier-analytics-charts">
            <article className="catalog-card supplier-analytics-chart">
              <div className="catalog-section-head">
                <div>
                  <h2>Compras por mes</h2>
                  <p>Evolución mensual del importe comprado.</p>
                </div>
              </div>

              {analytics.monthlySpend.length === 0 ? (
                <div className="catalog-state catalog-state-inline">
                  No hay fechas suficientes para calcular gasto mensual.
                </div>
              ) : (
                <div className="supplier-analytics-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analytics.monthlySpend}
                      margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#e5e7eb" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 11 }}
                        tickMargin={6}
                      />
                      <YAxis
                        width={76}
                        tick={{ fontSize: 11 }}
                        tickMargin={6}
                        tickFormatter={(value) =>
                          currencyFormatter.format(Number(value || 0))
                        }
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), "Gasto"]}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <Bar dataKey="totalSpend" name="Gasto" fill="#0f766e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </article>

            <article className="catalog-card supplier-analytics-chart">
              <div className="catalog-section-head">
                <div>
                  <h2>Top repuestos por gasto</h2>
                  <p>Repuestos con mayor importe comprado a este proveedor.</p>
                </div>
              </div>

              {analytics.topPartsBySpend.length === 0 ? (
                <div className="catalog-state catalog-state-inline">
                  No hay repuestos suficientes para representar el top de gasto.
                </div>
              ) : (
                <div className="supplier-top-parts-chart-wrap">
                  <div className="supplier-top-parts-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={analytics.topPartsBySpend}
                        layout="vertical"
                        margin={{ top: 6, right: 16, left: 10, bottom: 0 }}
                      >
                        <CartesianGrid stroke="#e5e7eb" horizontal={false} />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) =>
                            currencyFormatter.format(Number(value || 0))
                          }
                        />
                        <YAxis
                          dataKey="materialLabel"
                          type="category"
                          width={132}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => truncateText(value, 22)}
                        />
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), "Gasto"]}
                          labelFormatter={(label) => label}
                        />
                        <Bar
                          dataKey="total_spend"
                          name="Gasto"
                          fill="#0f766e"
                          radius={[0, 4, 4, 0]}
                          cursor="pointer"
                          onClick={(row) => {
                            if (row?.material_id) {
                              navigate(getPartDetailPath(row.material_id));
                            }
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                </div>
              )}
            </article>
          </section>

          <section className="catalog-card part-section-card">
            <div className="catalog-section-head">
              <div>
                <h2>Histórico de compras</h2>
                <p>
                  Histórico de compras realizadas a este proveedor. La ordenación por
                  columna se aplica en backend antes de paginar.
                </p>
              </div>
            </div>

            {purchaseHistory.length === 0 ? (
              <div className="catalog-state catalog-state-inline">
                No hay compras registradas para este proveedor.
              </div>
            ) : (
              <div>
                <div className="table-responsive catalog-table-scroll">
                  <table className="table table-compact mb-0 supplier-purchase-history-table">
                    <thead>
                      <tr>
                        {renderSortableHeader("date", "Fecha")}
                        {renderSortableHeader("material", "Material")}
                        <th>Descripción</th>
                        {renderSortableHeader("quantity", "Cantidad", "text-end")}
                        {renderSortableHeader("unit_price", "Precio unitario", "text-end")}
                        {renderSortableHeader("total_price", "Precio total", "text-end")}
                        <th>Batch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPurchaseHistory.map((row, index) => (
                        <tr key={`${row.date || "no-date"}-${row.material_id || "no-material"}-${row.batch_id || "no-batch"}-${(currentPurchaseHistoryPage - 1) * PURCHASE_HISTORY_PAGE_SIZE + index}`}>
                          <td className="supplier-purchase-history-date">{row.date || "-"}</td>
                          <td className="supplier-purchase-history-material">
                            {row.material_id ? (
                              <Link
                                className="catalog-link-button supplier-part-link"
                                to={getPartDetailPath(row.material_id)}
                                title={row.description || row.material_id}
                              >
                                {row.material_id}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            className="supplier-purchase-history-description"
                            title={row.description || ""}
                          >
                            {truncateText(row.description)}
                          </td>
                          <td className="text-end supplier-purchase-history-number">
                            {formatNumber(row.quantity)}
                          </td>
                          <td className="text-end supplier-purchase-history-money">
                            {formatCurrency(row.unit_price)}
                          </td>
                          <td className="text-end supplier-purchase-history-money">
                            {formatCurrency(row.total_price)}
                          </td>
                          <td className="supplier-purchase-history-batch">{row.batch_id || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="catalog-pagination">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentPurchaseHistoryPage === 1}
                    onClick={() =>
                      setPurchaseHistoryPage((currentPage) =>
                        Math.max(1, currentPage - 1),
                      )
                    }
                  >
                    Anterior
                  </button>
                  <span>
                    Página {currentPurchaseHistoryPage} de {totalPurchaseHistoryPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentPurchaseHistoryPage === totalPurchaseHistoryPages}
                    onClick={() =>
                      setPurchaseHistoryPage((currentPage) =>
                        Math.min(totalPurchaseHistoryPages, currentPage + 1),
                      )
                    }
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {documentsBlock}
      {documentPreviewModal}
      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="Eliminar documento"
        message={`El documento ${
          documentToDelete?.original_filename || "seleccionado"
        } dejará de estar disponible para consulta y descarga. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        danger
        onConfirm={handleDocumentDelete}
        onCancel={() => setDocumentToDelete(null)}
      />
    </div>
  );
};

export default SupplierDetail;
